import { AutomationActions, AutomationConditions, AutomationTrigger } from "@common-models/automation";
import { createSelector } from '@reduxjs/toolkit';

import { automationActionsSelectByQuery, automationConditionsSelectByQuery, automationTriggerSelectOneObjectByQuery, taskGroupsSelectByQuery } from "@common-reducers/DBServiceThunks";

import { getSelectedBoardId } from "@common-reducers/BoardsSelectors";
import { dynamicColumnsListOfIdsTypesAndTitles } from './TaskSelectors';
import { CommonRootState } from "@common/types/common-root-state-type";




const selectBoardId = (state: CommonRootState) => getSelectedBoardId(state);

export const getTrigger = createSelector(
  [(state: CommonRootState, automationId: string) => triggersSelector(state, automationId)],
  (triggers: AutomationTrigger | undefined) => {
    return triggers;
  }
);

export const triggersSelector = createSelector(
  [
    (state: CommonRootState) => state.DBAutomationTriggerReducer,
    (state: CommonRootState, automationId: string) => automationId,
  ],
  (DBAutomationTriggerReducer, automationId) => {
    return automationTriggerSelectOneObjectByQuery(
      DBAutomationTriggerReducer,
      {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
        automationId: automationId
      }
    );
  }
);


export const selectOptionPickersColumns = createSelector(
  dynamicColumnsListOfIdsTypesAndTitles,
  (columnsListOfIds) => {
    const typesToFilter = ['status-option-picker', 'option-picker', 'complexity-picker'];

    return columnsListOfIds
      .map(item => ({ id: item[0], type: item[1], name: item[2] as string }))
      .filter(item => item.type !== undefined && typesToFilter.includes(item.type.toString()));
  }
);


export const selectPersonPickerColumns = createSelector(
  dynamicColumnsListOfIdsTypesAndTitles,
  (columnsListOfIds) => {
    const typesToFilter = ['person-picker'];
    return columnsListOfIds
      .map(item => ({ id: item[0], type: item[1], name: item[2] as string }))
      .filter(item => item.type !== undefined && typesToFilter.includes(item.type.toString()));
  }
);

export const selectNumberColumns = createSelector(
  dynamicColumnsListOfIdsTypesAndTitles,
  (columnsListOfIds) => {
    const typesToFilter = ['number'];
    return columnsListOfIds
      .map(item => ({ id: item[0], type: item[1], name: item[2] as string }))
      .filter(item => item.type !== undefined && typesToFilter.includes(item.type.toString()));
  }
);

export const selectDatePickerColumns = createSelector(
  dynamicColumnsListOfIdsTypesAndTitles,
  (columnsListOfIds) => {
    const typesToFilter = ['datepicker', 'timeline-picker'];
    return columnsListOfIds
      .map(item => ({ id: item[0], type: item[1], name: item[2] as string }))
      .filter(item => item.type !== undefined && typesToFilter.includes(item.type.toString()));
  }
);
export const selectAllColumns = createSelector(
  dynamicColumnsListOfIdsTypesAndTitles,
  (columnsListOfIds) => {
    return columnsListOfIds
      .map(item => ({ id: item[0], type: item[1], name: item[2] as string }))
  }
);


export const getColumnsByType = (boardId: any, typesToFilter: string[]) => {
  return createSelector(
    (state: any) => dynamicColumnsListOfIdsTypesAndTitles(state, boardId),
    (columnsListOfIds) => {
      if (typesToFilter.length === 0) {
        return columnsListOfIds
          .map(item => ({ id: item[0], type: item[1], name: item[2] as string }));
      } else {
        return columnsListOfIds
          .map(item => ({ id: item[0], type: item[1], name: item[2] as string }))
          .filter(item => item.type !== undefined && typesToFilter.includes(item.type.toString()));
      }
    }
  );
}
export const getConditions = createSelector(
  [(state: CommonRootState, automationId: string) => conditionsSelector(state, automationId)],
  (conditions: AutomationConditions[]) => {
    return conditions;
  }
);

export const conditionsSelector = createSelector(
  [
    (state: CommonRootState) => state.DBAutomationConditionsReducer,
    (state: CommonRootState, automationId: string) => automationId,
  ],
  (DBAutomationConditionsReducer, automationId) => {
    return automationConditionsSelectByQuery(
      DBAutomationConditionsReducer,
      {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
        automationId: automationId
      }
    );
  }
);

export const selectActionsLength = createSelector(
  (state: CommonRootState) => state.AutomationReducer.actions,
  (actions) => actions.length
);

export const getActions = createSelector(
  [(state: CommonRootState, automationId: string) => actionsSelector(state, automationId)],
  (actions: AutomationActions[]) => {
    return actions;
  }
);


export const actionsSelector = createSelector(
  [
    (state: CommonRootState) => state.DBAutomationActionsReducer,
    (state: CommonRootState, automationId: string) => automationId,
  ],
  (DBAutomationActionsReducer, automationId) => {
    return automationActionsSelectByQuery(
      DBAutomationActionsReducer,
      {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
        automationId: automationId
      }
    );
  }
);



const selectTaskGroups = createSelector(
  [selectBoardId, (state: CommonRootState) => state.DBTaskgroupsReducer],
  (boardId, DBTaskgroupsReducer) => taskGroupsSelectByQuery(DBTaskgroupsReducer,
    {
      boardId: boardId,
      $or: [{ deleted: { $exists: false } }, { deleted: false }]
    })
);

export const selectMappedTaskGroups = createSelector(
  selectTaskGroups,
  (groups) => groups.map(group => {
    const { name, ...rest } = group;
    return { label: name ?? '', ...rest };
  })
);